import { Link } from "react-router-dom";
import { ExternalLink, Sparkles } from "lucide-react";
import { EmptyState } from "@/components/v2/EmptyState";

type TopService = {
  serviceId?: string;
  serviceName?: string;
  bookings?: number;
  revenue?: number | string;
};

/**
 * Ranked list of top services in the window. Each row shows: rank, name
 * (links to /services/:id), bookings count, revenue, and an inline progress
 * bar scaled to the top service's revenue (or bookings if revenue is zero).
 */
export function TopServicesList({
  services,
  currency = "PHP",
}: {
  services: TopService[];
  currency?: string;
}) {
  const peakRevenue = services.reduce(
    (max, s) => Math.max(max, Number(s.revenue ?? 0)),
    0,
  );
  const peakBookings = services.reduce(
    (max, s) => Math.max(max, s.bookings ?? 0),
    0,
  );
  const usingRevenueScale = peakRevenue > 0;

  return (
    <section className="rounded-xl border bg-card p-5 flex flex-col h-full">
      <header className="mb-4">
        <h2 className="text-base font-semibold tracking-tight">Top services</h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Ranked by booking count. Bar shows{" "}
          {usingRevenueScale ? "revenue" : "bookings"} share vs the leader.
        </p>
      </header>

      {services.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={Sparkles}
            title="No services booked"
            description="When a service is booked it'll show up in the ranking here."
          />
        </div>
      ) : (
        <ol className="flex-1 space-y-3">
          {services.map((s, i) => {
            const rev = Number(s.revenue ?? 0);
            const pct = usingRevenueScale
              ? peakRevenue > 0
                ? Math.round((rev / peakRevenue) * 100)
                : 0
              : peakBookings > 0
                ? Math.round(((s.bookings ?? 0) / peakBookings) * 100)
                : 0;
            return (
              <li key={s.serviceId ?? i} className="space-y-1.5">
                <div className="flex items-baseline gap-3">
                  <span className="text-[11px] font-mono text-muted-foreground tabular-nums w-5 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0 flex-1">
                    {s.serviceId ? (
                      <Link
                        to={`/services/${s.serviceId}`}
                        className="text-sm font-medium hover:underline truncate inline-flex items-center gap-1"
                      >
                        {s.serviceName ?? "—"}
                        <ExternalLink className="h-3 w-3 text-muted-foreground/70" />
                      </Link>
                    ) : (
                      <span className="text-sm font-medium truncate">
                        {s.serviceName ?? "—"}
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                    {s.bookings ?? 0} booking{(s.bookings ?? 0) === 1 ? "" : "s"}
                  </span>
                  <span className="text-sm font-semibold tabular-nums shrink-0 w-24 text-right">
                    {currency}{" "}
                    {rev.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
                <div className="ml-8 h-1.5 rounded-full bg-muted/60 overflow-hidden">
                  <div
                    className="h-full bg-primary/70"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
